import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/60"
          : "bg-transparent"
      }`}
    >
      <div className="px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link
            to="/"
            className={`font-heading text-xl md:text-2xl font-bold tracking-tight whitespace-nowrap transition-colors duration-300 ${
              scrolled ? "text-foreground-950" : "text-white"
            }`}
          >
            kakobuy<span className={scrolled ? "text-primary-500" : "text-primary-300"}>sheet</span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap ${
                scrolled
                  ? "text-foreground-700 hover:text-primary-500"
                  : "text-white/90 hover:text-white"
              }`}
            >
              {t("nav.home")}
            </Link>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1 text-sm font-medium transition-colors duration-300 whitespace-nowrap ${
                  scrolled
                    ? "text-foreground-700 hover:text-primary-500"
                    : "text-white/90 hover:text-white"
                }`}
              >
                {t("nav.categories")}
                <i
                  className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${
                    dropdownOpen ? "rotate-180" : ""
                  }`}
                ></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-background-50 rounded-lg border border-background-200/70 py-2 overflow-hidden">
                  {categories.map((cat) => (
                    <a
                      key={cat.nameKey}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-500 transition-colors whitespace-nowrap"
                      onClick={() => setDropdownOpen(false)}
                    >
                      {t(cat.nameKey)}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/guides/complete-kakobuy-spreadsheet-guide"
              className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap ${
                scrolled
                  ? "text-foreground-700 hover:text-primary-500"
                  : "text-white/90 hover:text-white"
              }`}
            >
              {t("nav.guides")}
            </Link>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap ${
                scrolled
                  ? "text-foreground-700 hover:text-primary-500"
                  : "text-white/90 hover:text-white"
              }`}
            >
              {t("nav.contact")}
            </a>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              {t("hero.cta.spreadsheet")}
              <i className="ri-arrow-right-line text-sm"></i>
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-9 h-9 flex items-center justify-center transition-colors cursor-pointer ${
              scrolled ? "text-foreground-950" : "text-white"
            }`}
          >
            <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60 px-4 py-4 space-y-1">
          <Link
            to="/"
            onClick={() => setMobileOpen(false)}
            className="block py-3 text-sm font-medium text-foreground-700 hover:text-primary-500 whitespace-nowrap"
          >
            {t("nav.home")}
          </Link>
          <div className="py-2">
            <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-2 px-1 whitespace-nowrap">
              {t("nav.categories")}
            </p>
            {categories.map((cat) => (
              <a
                key={cat.nameKey}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="block py-2 px-1 text-sm text-foreground-600 hover:text-primary-500 whitespace-nowrap"
                onClick={() => setMobileOpen(false)}
              >
                {t(cat.nameKey)}
              </a>
            ))}
          </div>
          <Link
            to="/guides/complete-kakobuy-spreadsheet-guide"
            onClick={() => setMobileOpen(false)}
            className="block py-3 text-sm font-medium text-foreground-700 hover:text-primary-500 whitespace-nowrap"
          >
            {t("nav.guides")}
          </Link>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="block py-3 text-sm font-medium text-foreground-700 hover:text-primary-500 whitespace-nowrap"
            onClick={() => setMobileOpen(false)}
          >
            {t("nav.contact")}
          </a>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-1.5 mt-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap"
          >
            {t("hero.cta.spreadsheet")}
            <i className="ri-arrow-right-line text-sm"></i>
          </a>
        </div>
      )}
    </nav>
  );
}